import { Button } from './Button'
import { Checkbox } from './Checkbox'
import { Select } from './Select'
import { TextInput } from './TextInput'
import { useNls } from '../../nls/nlsProvider'
import {
  isNeverHideableKey,
  type AiControlMode,
  type ChatControl,
  type FunPackControl,
  type MemberControls,
} from '../../types/memberControls'
import { PAGE_GROUPS, PAGE_LABEL_KEYS } from '../../utils/memberControlsPageLabels'
import './MemberControlsEditor.css'

export interface MemberControlsEditorProps {
  /** If editing a member with a profile, the name of the assigned profile */
  profileName?: string | null
  /** If editing a member with a profile, the controls defined on that profile */
  profileControls?: MemberControls | null
  /** The current controls (either member overrides or the profile's controls) */
  controls: MemberControls
  /** Callback fired when any control changes */
  onChange: (patch: MemberControls) => void
  /** When true, all controls are disabled (e.g. viewer cannot manage or admin target) */
  disabled?: boolean
  /** Whether to show the AI control row. False for helper accounts (A10) */
  showAi?: boolean
  /** When true, editing a profile definition itself (no override tags or reset buttons) */
  isProfileMode?: boolean
  /** Callback to clear an override for a specific control key */
  onResetControl?: (key: keyof MemberControls) => void
  /** Optional custom test id */
  testId?: string
}

export const MemberControlsEditor = ({
  profileName,
  profileControls,
  controls,
  onChange,
  disabled = false,
  showAi = true,
  isProfileMode = false,
  onResetControl,
  testId = 'member-controls-editor',
}: MemberControlsEditorProps) => {
  const { t } = useNls()

  // 1. Fun pack
  const hasFunPackOverride = !isProfileMode && controls.funPack !== undefined && controls.funPack !== null
  const effectiveFunPack: FunPackControl = controls.funPack ?? profileControls?.funPack ?? 'INHERIT'
  const funPackOptions: Array<{ value: FunPackControl; label: string }> = [
    { value: 'INHERIT', label: t('NLSControlsFunPackInherit') },
    { value: 'OFF', label: t('NLSControlsFunPackOff') },
  ]

  // 2. Chat
  const hasChatOverride = !isProfileMode && controls.chat !== undefined && controls.chat !== null
  const effectiveChat: ChatControl = controls.chat ?? profileControls?.chat ?? 'FULL'
  const chatOptions: Array<{ value: ChatControl; label: string }> = [
    { value: 'FULL', label: t('NLSControlsChatFull') },
    { value: 'ANNOUNCEMENTS_ONLY', label: t('NLSControlsChatAnnouncements') },
    { value: 'NO_DM', label: t('NLSControlsChatNoDm') },
    { value: 'OFF', label: t('NLSControlsChatOff') },
  ]

  // 3. AI
  const hasAiOverride = !isProfileMode && controls.ai !== undefined && controls.ai !== null
  const effectiveAiMode: AiControlMode = controls.ai?.mode ?? profileControls?.ai?.mode ?? 'INHERIT'
  const effectiveDailyCredits =
    controls.ai?.dailyCredits ?? profileControls?.ai?.dailyCredits ?? 50
  const aiModeOptions: Array<{ value: AiControlMode; label: string }> = [
    { value: 'INHERIT', label: t('NLSControlsAiInherit') },
    { value: 'OFF', label: t('NLSControlsAiOff') },
    { value: 'BUDGET', label: t('NLSControlsAiBudget') },
  ]

  // 4. Visible Pages (tiles lists hidden page keys)
  const hasTilesOverride = !isProfileMode && controls.tiles !== undefined && controls.tiles !== null
  const effectiveTiles: readonly string[] = Array.isArray(controls.tiles)
    ? controls.tiles
    : Array.isArray(profileControls?.tiles)
    ? profileControls.tiles
    : []

  const handleTogglePage = (pageKey: string, nextVisible: boolean) => {
    if (disabled || isNeverHideableKey(pageKey)) return
    const currentHidden = new Set(effectiveTiles)
    if (nextVisible) {
      currentHidden.delete(pageKey)
    } else {
      currentHidden.add(pageKey)
    }
    onChange({
      ...controls,
      tiles: Array.from(currentHidden),
    })
  }

  return (
    <div className="memberControlsEditor" data-testid={testId}>
      {/* Fun Pack Row */}
      <div className="memberControlsEditor__row" data-testid="control-row-fun-pack">
        <div className="memberControlsEditor__rowHeader">
          <span className="memberControlsEditor__label">{t('NLSControlsFunPack')}</span>
          {!isProfileMode ? (
            <div className="memberControlsEditor__source">
              {hasFunPackOverride ? (
                <>
                  <span className="memberControlsEditor__sourceBadge memberControlsEditor__sourceBadge--override">
                    {t('NLSControlsYourOverride')}
                  </span>
                  {profileName && onResetControl ? (
                    <Button
                      variant="link"
                      size="sm"
                      disabled={disabled}
                      onClick={() => onResetControl('funPack')}
                      aria-label={`Reset fun pack to profile ${profileName}`}
                    >
                      {t('NLSControlsResetToProfile')}
                    </Button>
                  ) : null}
                </>
              ) : profileName ? (
                <span className="memberControlsEditor__sourceBadge">
                  {t('NLSControlsFromProfile').replace('{name}', profileName)}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="memberControlsEditor__controls">
          <Select<FunPackControl>
            trigger="pill"
            value={effectiveFunPack}
            options={funPackOptions}
            disabled={disabled}
            onChange={(val) => onChange({ ...controls, funPack: val })}
            ariaLabel="Fun pack control"
          />
        </div>
      </div>

      {/* Chat Row */}
      <div className="memberControlsEditor__row" data-testid="control-row-chat">
        <div className="memberControlsEditor__rowHeader">
          <span className="memberControlsEditor__label">{t('NLSControlsChat')}</span>
          {!isProfileMode ? (
            <div className="memberControlsEditor__source">
              {hasChatOverride ? (
                <>
                  <span className="memberControlsEditor__sourceBadge memberControlsEditor__sourceBadge--override">
                    {t('NLSControlsYourOverride')}
                  </span>
                  {profileName && onResetControl ? (
                    <Button
                      variant="link"
                      size="sm"
                      disabled={disabled}
                      onClick={() => onResetControl('chat')}
                      aria-label={`Reset chat to profile ${profileName}`}
                    >
                      {t('NLSControlsResetToProfile')}
                    </Button>
                  ) : null}
                </>
              ) : profileName ? (
                <span className="memberControlsEditor__sourceBadge">
                  {t('NLSControlsFromProfile').replace('{name}', profileName)}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="memberControlsEditor__controls">
          <Select<ChatControl>
            trigger="pill"
            value={effectiveChat}
            options={chatOptions}
            disabled={disabled}
            onChange={(val) => onChange({ ...controls, chat: val })}
            ariaLabel="Chat control"
          />
        </div>
      </div>

      {/* AI Row */}
      {showAi ? (
        <div className="memberControlsEditor__row" data-testid="control-row-ai">
          <div className="memberControlsEditor__rowHeader">
            <span className="memberControlsEditor__label">{t('NLSControlsAi')}</span>
            {!isProfileMode ? (
              <div className="memberControlsEditor__source">
                {hasAiOverride ? (
                  <>
                    <span className="memberControlsEditor__sourceBadge memberControlsEditor__sourceBadge--override">
                      {t('NLSControlsYourOverride')}
                    </span>
                    {profileName && onResetControl ? (
                      <Button
                        variant="link"
                        size="sm"
                        disabled={disabled}
                        onClick={() => onResetControl('ai')}
                        aria-label={`Reset AI to profile ${profileName}`}
                      >
                        {t('NLSControlsResetToProfile')}
                      </Button>
                    ) : null}
                  </>
                ) : profileName ? (
                  <span className="memberControlsEditor__sourceBadge">
                    {t('NLSControlsFromProfile').replace('{name}', profileName)}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="memberControlsEditor__controls">
            <Select<AiControlMode>
              trigger="pill"
              value={effectiveAiMode}
              options={aiModeOptions}
              disabled={disabled}
              onChange={(val) => {
                if (val === 'BUDGET') {
                  onChange({
                    ...controls,
                    ai: { mode: 'BUDGET', dailyCredits: effectiveDailyCredits || 50 },
                  })
                } else {
                  onChange({ ...controls, ai: { mode: val } })
                }
              }}
              ariaLabel="AI control mode"
            />
            {effectiveAiMode === 'BUDGET' ? (
              <div className="memberControlsEditor__budgetInput">
                <TextInput
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  value={String(effectiveDailyCredits)}
                  disabled={disabled}
                  onChange={(val) => {
                    const parsed = Number.parseInt(val, 10)
                    if (Number.isFinite(parsed) && parsed >= 1) {
                      onChange({
                        ...controls,
                        ai: { mode: 'BUDGET', dailyCredits: parsed },
                      })
                    }
                  }}
                  aria-label={t('NLSControlsAiDailyCreditsAria')}
                />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Visible Pages Row */}
      <div className="memberControlsEditor__row" data-testid="control-row-visible-pages">
        <div className="memberControlsEditor__rowHeader">
          <span className="memberControlsEditor__label">{t('NLSControlsVisiblePages')}</span>
          {!isProfileMode ? (
            <div className="memberControlsEditor__source">
              {hasTilesOverride ? (
                <>
                  <span className="memberControlsEditor__sourceBadge memberControlsEditor__sourceBadge--override">
                    {t('NLSControlsYourOverride')}
                  </span>
                  {profileName && onResetControl ? (
                    <Button
                      variant="link"
                      size="sm"
                      disabled={disabled}
                      onClick={() => onResetControl('tiles')}
                      aria-label={`Reset visible pages to profile ${profileName}`}
                    >
                      {t('NLSControlsResetToProfile')}
                    </Button>
                  ) : null}
                </>
              ) : profileName ? (
                <span className="memberControlsEditor__sourceBadge">
                  {t('NLSControlsFromProfile').replace('{name}', profileName)}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="memberControlsEditor__desc">{t('NLSControlsVisiblePagesDesc')}</div>
        <div className="memberControlsEditor__pageGroups">
          {PAGE_GROUPS.map((group) => (
            <div
              key={group.id}
              className="memberControlsEditor__pageGroup"
              data-testid={`page-group-${group.id}`}
            >
              <div className="memberControlsEditor__pageGroupTitle">{t(group.titleKey)}</div>
              <div className="memberControlsEditor__pageGrid">
                {group.keys.map((key) => {
                  const locked = isNeverHideableKey(key)
                  const isVisible = locked || !effectiveTiles.includes(key)
                  const labelKey = PAGE_LABEL_KEYS[key]
                  const labelText = labelKey ? t(labelKey) : key
                  return (
                    <div key={key} className="memberControlsEditor__pageItem">
                      <Checkbox
                        checked={isVisible}
                        disabled={disabled || locked}
                        onChange={(nextChecked) => handleTogglePage(key, nextChecked)}
                        aria-label={labelText}
                        label={
                          <span className="memberControlsEditor__pageLabel">
                            <span>{labelText}</span>
                            {locked ? (
                              <span
                                className="memberControlsEditor__lockedBadge"
                                title={t('NLSControlsPageLocked')}
                              >
                                {t('NLSControlsPageLocked')}
                              </span>
                            ) : null}
                          </span>
                        }
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
